import { __assign } from 'tslib';
import React, { useRef, useMemo, useEffect } from 'react';
import { ResetUndoIcon } from '@panwds/icons';
import { testIdGen, Tooltip, Button } from '@panwds/react-ui';
import { ParentSize } from '@visx/responsive';
import { useFilters } from '../../hooks/useFilters/useFilters.esm.js';
import { actions } from '../../state/actions.esm.js';
import '../../state/reducer.esm.js';
import { FilterProvider } from '../FilterProvider/FilterProvider.esm.js';
import { FilterPills } from './FilterPills.esm.js';
import { useHashParams } from './useHashParams.esm.js';

var defaultConfig = {
    deserialize: function (value) { return value; },
    serialize: function (value) { return value; },
    static: false
};
var FilterBarWithSize = function (_a) {
    var _b, _c;
    var actionButtons = _a.actions, dataMetrics = _a.dataMetrics, dataTestId = _a.dataTestId, deserialize = _a.deserialize, serialize = _a.serialize, _d = _a.disabled, disabled = _d === void 0 ? false : _d, defaultState = _a.defaultState, _e = _a.enableQueryParams, enableQueryParams = _e === void 0 ? false : _e, filterConfig = _a.filters, _f = _a.isLoading, isLoading = _f === void 0 ? false : _f, name = _a.name, parentWidth = _a.parentWidth, _g = _a.saveOnUnmount, saveOnUnmount = _g === void 0 ? true : _g;
    var actionsRef = useRef(null);
    var _h = useFilters(name), dispatch = _h.dispatch, displayedFilters = _h.displayedFilters, resetFilters = _h.resetFilters;
    var normalizedFilterConfig = useMemo(function () {
        return filterConfig.reduce(function (currentFilters, filter) {
            var _a;
            return __assign(__assign({}, currentFilters), (_a = {}, _a[filter.name] = __assign(__assign(__assign({}, defaultConfig), filter), { isDisplayed: displayedFilters.includes(filter.name) }), _a));
        }, {});
    }, [name, filterConfig, displayedFilters]);
    var params = useHashParams(name, enableQueryParams, normalizedFilterConfig, deserialize, serialize);
    useEffect(function () {
        if (saveOnUnmount)
            return;
        return function () {
            return dispatch({
                type: actions.remove,
                payload: {
                    name: name
                }
            });
        };
    }, [name]);
    useEffect(function () {
        if (isLoading)
            return;
        dispatch({
            type: actions.init,
            payload: {
                name: name,
                defaultState: defaultState,
                filterConfig: filterConfig,
                initialFilters: params
            }
        });
    }, [isLoading, name, filterConfig]);
    return (React.createElement(FilterProvider, { name: name },
        React.createElement("div", { "data-metrics": dataMetrics, "data-testid": dataTestId || "filter-bar-".concat(testIdGen(name)), className: "tw-flex tw-text-default dark:tw-text-dark-bg" },
            React.createElement(FilterPills, { normalizedFilterConfig: normalizedFilterConfig, parentWidth: parentWidth - ((_c = (_b = actionsRef.current) === null || _b === void 0 ? void 0 : _b.offsetWidth) !== null && _c !== void 0 ? _c : 0), disabled: disabled, isLoading: isLoading }),
            React.createElement("div", { className: "tw-flex tw-space-x-1 tw-items-center", ref: actionsRef },
                actionButtons,
                React.createElement(Tooltip, { label: "Reset Filters" },
                    React.createElement(Button, { addClassName: "tw-whitespace-nowrap", "aria-label": "Reset Filters", onClick: resetFilters, icon: React.createElement(ResetUndoIcon, { className: "tw-absolute", size: "md" }), disabled: disabled || isLoading, appearance: "tertiary" }))))));
};
/**
 *
 * The FilterBar is responsible for displaying active filters, and hiding
 * inactive filters.
 *
 * ### Import Guide
 *
 * ```jsx
 * import { FilterBar, GlobalFilterProvider, SelectFilter } from "@panwds/react-filters";
 * ```
 *
 * GlobalFilterProvider should already be set in the Root component of your project,
 * but when writing tests, you'll need to wrap your component with GlobalFilterProvider.
 */
var FilterBar = function (props) {
    return (React.createElement("div", null,
        React.createElement(ParentSize, null, function (parent) { return (React.createElement(FilterBarWithSize, __assign({}, props, { parentWidth: parent.width }))); })));
};

export { FilterBar };
//# sourceMappingURL=FilterBar.esm.js.map
